<script setup lang="ts">
const props = defineProps<{
    modelValue?: boolean | Array<string>;
    value?: string | number;
    hasError?: boolean;
    checked?: boolean;
    noIcon?: boolean;
    id?: string;
    disabled?: boolean;
  }>();

const { modelValue, checked, id } = toRefs(props);
const nativeInput = ref<HTMLInputElement>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean | Array<string>): void;
    (e: 'focus', value: FocusEvent): void;
    (e: 'blur', value: FocusEvent): void;
  }>();

const handleChange = (event: Event) => {
  if (modelValue.value === undefined || props.value === undefined) {
    emit('update:modelValue', (event?.target as HTMLInputElement).checked);
    return;
  }

  const strValue = String(props.value);

  if (Array.isArray(modelValue.value)) {
    const newValue = modelValue.value.slice();

    if (modelValue.value.includes(strValue)) {
      // Remove value from array
      newValue.splice(newValue.indexOf(strValue), 1);
      console.log('remove',strValue, newValue);
    } else {
      // Add value to array
      newValue.push(strValue);
      console.log('add',strValue, newValue);
    }
    emit('update:modelValue', newValue);
  } else {
    emit('update:modelValue', (event?.target as HTMLInputElement).checked);
  }
};

const valueIsInModelValue = computed(() => {
  if (modelValue.value === undefined || props.value === undefined) {
    return false;
  }

  if (Array.isArray(modelValue.value)) {
    return modelValue.value.includes(String(props.value));
  }

  return modelValue.value;
});

const proxyClick = () => {
  nativeInput.value?.click();
};
</script>

<template>
  <div
    :class="{
      'm-input-checkbox': true,
      'm-input-checkbox--error': hasError,
      'no-icon': noIcon
    }"
  >
    <input
      :id="id"
      ref="nativeInput"
      class="native-input"
      type="checkbox"
      :checked="checked || valueIsInModelValue"
      :disabled="disabled"
      @input="handleChange($event)"
      @focus="$emit('focus', $event)"
      @blur="$emit('blur', $event)"
    >

    <div
      class="proxy-input"
      :class="{'disabled': disabled}"
      @click.stop.prevent="proxyClick"
    />
  </div>
</template>

<style lang="scss">
  .m-input-checkbox {
    display: inline-block;
    vertical-align: middle;

    .native-input {
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      height: 1px;
      overflow: hidden;
      position: absolute;
      white-space: nowrap;
      width: 1px;

      &:focus + .proxy-input {
        box-shadow: 0 0 0 1px white,
          0 0 0 3px var(--palette-blue-50);
      }

      &:checked + .proxy-input {
        background-color: var(--palette-blue-50);
        border-color: var(--palette-blue-50);

        &::after {
          background-image: url("data:image/svg+xml,%3Csvg width='13' height='12' viewBox='0 0 13 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M11.8599 0.652529C12.3076 0.975886 12.4085 1.60097 12.0851 2.0487L5.58511 11.0487C5.40671 11.2957 5.12544 11.4479 4.82108 11.4621C4.51673 11.4763 4.22249 11.351 4.02185 11.1217L0.521851 7.12171C0.158169 6.70608 0.200287 6.07431 0.615923 5.71063C1.03156 5.34695 1.66332 5.38907 2.027 5.8047L4.6995 8.85898L10.4637 0.877718C10.7871 0.429992 11.4122 0.329171 11.8599 0.652529Z' fill='white'/%3E%3C/svg%3E%0A");
          transform: scale(1);
        }
      }

    }

    &.no-icon .native-input:checked + .proxy-input::after {
      background-image: none;
      border: 2px solid white;
      border-radius: 0.15em;
      width: 100%;
      height: 100%;
    }

    .proxy-input {
      position: relative;
      display: inline-block;
      width: 1.25em;
      height: 1.25em;
      border: 2px solid var(--palette-neutral-40);
      border-radius: 0.25em;
      background-color: var(--palette--neutral-10);
      transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out;
      cursor: pointer;

      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-repeat: no-repeat;
        background-size: 100%;
        border-radius: 0.25rem;
        transform: scale(0);
        transition: transform 0.2s ease-in-out;
      }

      &.disabled {
        cursor: not-allowed;
        border-color: var(--palette-neutral-30);
        background-color: var(--palette-neutral-20);
      }
    }

    &--error {
      border-color: var(--color-negative);
    }
  }
</style>